import { Injectable } from '@angular/core'
import { QuestionAnswer } from 'src/app/interfaces/question'
import { customLocalStorage } from 'src/utils/utils'

export const QUESTION_KEYS = ['questionId', 'quizType', 'type', 'hostQuizReport']
export const QUESTION_STORE_NAME = `questions`
// host는 무조건 로그인 해야 가능하기 때문에 guest일 때만 저장.
export type QuizReportStoreName =
  | 'mbtiHostQuizReport'
  | `mbtiGuestQuizReport`
  | `ftGuestQuizReport`
  | 'attachmentTypeGuestQuizReport'
  | 'attachmentTypeHostQuizReport'
  | 'guessMeGuestQuizReport'
  | 'idealTypeGuestQuizReport'
  | 'hspHostQuizReport'
  | 'hspGuestQuizReport'
  | 'egenTetoHostQuizReport'
  | 'egenTetoGuestQuizReport'

export interface QuestionStore {
  id?: string
  questionId: number
  quizType: 'host' | 'guest'
  type: 'pre-question' | 'mbti'
  answer: string | number
  hostQuizReport?: number
}

@Injectable({
  providedIn: 'root',
})
export class DataBaseService {
  private get isLocalStorageAvailable(): boolean {
    return navigator?.cookieEnabled ?? false
  }

  constructor() {}

  mbtiQuestionStoreAdaptor(
    answers: QuestionAnswer[],
    quizType: 'host' | 'guest',
    questionType: 'pre-question' | 'mbti',
    hostQuizReport?: number
  ): QuestionStore[] {
    return answers.map((answer) => {
      return {
        questionId: answer.questionAnswerSet[0].question.id,
        quizType,
        answer: answer.value!,
        type: questionType,
        hostQuizReport,
      }
    })
  }

  setItem(storeName: string, newValue: any, keys?: string[]): void {
    if (!this.isLocalStorageAvailable) {
      return
    }
    let currentItems: string | null = customLocalStorage.getItem(storeName)
    let data: any[]
    if (currentItems === null) {
      data = []
    } else {
      data = JSON.parse(currentItems)
    }

    if (Array.isArray(newValue)) {
      const tobeAdded: any[] = []
      for (const newItem of newValue) {
        let updated = false
        data = data.map((currentItem) => {
          // object가 일치하는지 파악.
          if (this.objectEquals(currentItem, newItem, keys)) {
            updated = true
            return newItem
          } else {
            return currentItem
          }
        })
        // object
        if (!updated) {
          tobeAdded.push(newItem)
        }
      }
      data.push(...tobeAdded)
    } else {
      let updated = false
      data = data.map((currentItem) => {
        // object가 일치하는지 파악.
        if (this.objectEquals(currentItem, newValue, keys)) {
          updated = true
          return newValue
        } else {
          return currentItem
        }
      })
      if (!updated) {
        data.push(newValue)
      }
    }

    customLocalStorage.setItem(storeName, JSON.stringify(data))
  }

  getAllItems(storeName: string): any[] {
    if (!this.isLocalStorageAvailable) {
      return []
    }
    let items: string | null = customLocalStorage.getItem(storeName)
    if (items !== null) {
      return JSON.parse(items)
    } else {
      return []
    }
  }

  getItem(storeName: string, data: any, keys?: string[]): any | undefined {
    if (!this.isLocalStorageAvailable) {
      return
    }
    let items: string | null = customLocalStorage.getItem(storeName)
    if (items !== null) {
      const currentItems = JSON.parse(items)
      for (const item of currentItems) {
        if (this.objectEquals(item, data, keys)) {
          return item
        }
      }
    }
    return
  }

  async removeItem(storeName: string, removeValue: any, keys?: string[]): Promise<any> {
    if (!this.isLocalStorageAvailable) {
      return
    }
    let currentItems: string | null = customLocalStorage.getItem(storeName)
    let data: any[]
    if (currentItems === null) {
      data = []
    } else {
      data = JSON.parse(currentItems)
    }

    if (Array.isArray(removeValue)) {
      data = data.filter((item) => {
        for (const removeItem of removeValue) {
          if (this.objectEquals(item, removeItem, keys)) {
            return false
          }
        }
        return true
      })
    } else {
      data = data.filter((item) => {
        if (this.objectEquals(item, removeValue, keys)) {
          return false
        }
        return true
      })
    }
    customLocalStorage.setItem(storeName, JSON.stringify(data))
  }

  clearData(storeName: QuizReportStoreName): void {
    if (!this.isLocalStorageAvailable) {
      return
    }
    customLocalStorage.removeItem(storeName)
  }

  getQuizReport(storeName: QuizReportStoreName, quizReportId: number): any | undefined {
    const data = this.getStoreData(storeName)
    return data.find((item) => item.id === quizReportId)
  }

  getAllQuizReport(storeName: QuizReportStoreName): any[] {
    return this.getStoreData(storeName)
  }

  setQuizReport(storeName: QuizReportStoreName, quizReport: any): void {
    const prevData = this.getStoreData(storeName)
    if (!prevData.find((item) => item.id === quizReport.id)) {
      customLocalStorage.setItem(storeName, JSON.stringify([...prevData, quizReport]))
    }
  }

  private getStoreData(storeName: string): any[] {
    const storeData: string | null = customLocalStorage.getItem(storeName)
    return storeData !== null ? JSON.parse(storeData) : []
  }

  private objectEquals(a: object, b: object, keys?: string[]): boolean {
    if (typeof a !== 'object' || typeof b !== 'object') return false
    let objA = {}
    let objB = {}
    if (keys !== undefined) {
      for (const keyA of Object.keys(a)) {
        if (keys.includes(keyA)) {
          objA[keyA] = a[keyA]
        }
      }

      for (const keyB of Object.keys(b)) {
        if (keys.includes(keyB)) {
          objB[keyB] = b[keyB]
        }
      }
    } else {
      objA = { ...a }
      objB = { ...b }
    }
    // 인자값의 Type이 object가 아닐경우 false를 리턴한다.
    // Type을 String으로 변환한다.
    const objStrA = JSON.stringify(objA)
    var objStrB = JSON.stringify(objB)

    return objStrA === objStrB
  }
}
